import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Modal, Form, Badge, Alert, Spinner, ProgressBar } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faBook,
  faBookOpen,
  faCloudDownloadAlt,
  faTimesCircle,
  faPencilAlt,
  faTrash,
  faUpload,
  faHdd,
  faCog,
  faPlay,
  faChevronRight,
  faCheckCircle,
  faFileUpload,
  faCloud
} from "@fortawesome/free-solid-svg-icons";
import "../styles/Cookbooks.scss";
import {
  getCachedStatusBulk,
  getCacheStats,
  cacheCookbook,
  removeCachedCookbook,
  clearAllCache,
  formatBytes,
  MAX_CACHE_SIZE
} from '../services/cookbookCache';

function Cookbooks() {
  const navigate = useNavigate();

  // State management
  const [cookbooks, setCookbooks] = useState([]);
  const [filteredCookbooks, setFilteredCookbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    title: "",
    tags: "",
    notes: ""
  });
  const fileInputRef = React.useRef(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCookbook, setSelectedCookbook] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: "",
    tags: "",
    notes: ""
  });

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cookbookToDelete, setCookbookToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [allTags, setAllTags] = useState([]);

  // Sorting state
  const [sortBy, setSortBy] = useState("lastAccessed");

  // Google Drive state
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState("");
  const [showGooglePicker, setShowGooglePicker] = useState(false);
  const [showGoogleAuthModal, setShowGoogleAuthModal] = useState(false);
  const [googleDriveFiles, setGoogleDriveFiles] = useState([]);
  const [loadingGoogleFiles, setLoadingGoogleFiles] = useState(false);
  const [selectedDriveFile, setSelectedDriveFile] = useState(null);
  const [linkFormData, setLinkFormData] = useState({
    title: "",
    tags: "",
    notes: ""
  });
  const [linking, setLinking] = useState(false);

  // Cache state
  const [cachedStatus, setCachedStatus] = useState({});
  const [cacheStats, setCacheStats] = useState({ totalSize: 0, maxSize: MAX_CACHE_SIZE, bookCount: 0, books: [] });
  const [downloadingForOffline, setDownloadingForOffline] = useState({});
  const [showCacheManagement, setShowCacheManagement] = useState(false);
  const [showUncacheModal, setShowUncacheModal] = useState(false);
  const [cookbookToUncache, setCookbookToUncache] = useState(null);

  // Reading progress state
  const [readingProgress, setReadingProgress] = useState({});

  // Fetch cookbooks on mount
  useEffect(() => {
    fetchCookbooks();
    fetchTags();
    checkGoogleAuth();
    refreshCacheStats();
    fetchReadingProgress();

    // Refetch progress when window regains focus (user returns from reader)
    const handleFocus = () => {
      fetchReadingProgress();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Update cache status when cookbooks change
  useEffect(() => {
    if (cookbooks.length > 0) {
      refreshCacheStatus();
    }
  }, [cookbooks]);

  // Filter and sort cookbooks when filters/sorting change
  useEffect(() => {
    filterCookbooks();
  }, [searchQuery, selectedTag, cookbooks, sortBy, readingProgress]);

  const fetchCookbooks = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks`,
        { credentials: "include" }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch cookbooks");
      }

      const data = await response.json();
      setCookbooks(data.cookbooks || []);
      setError("");
    } catch (err) {
      setError(err.message);
      console.error("Error fetching cookbooks:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/tags/all`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setAllTags(data.tags || []);
      }
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };

  const fetchReadingProgress = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/progress/all`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        // Convert array to object keyed by cookbook_id
        const progressMap = {};
        (data.progress || []).forEach(p => {
          progressMap[p.cookbook_id] = p;
        });
        setReadingProgress(progressMap);
      }
    } catch (err) {
      console.error("Error fetching reading progress:", err);
    }
  };

  // Helper to calculate progress percentage
  const getProgressPercentage = (cookbookId, cookbook) => {
    const progress = readingProgress[cookbookId];
    if (!progress) return 0;

    // Use cookbook's file_type since progress.file_type might not be set
    const fileType = cookbook?.file_type || progress.file_type;

    if (fileType === 'pdf' && progress.total_pages && progress.page_number) {
      return Math.round((progress.page_number / progress.total_pages) * 100);
    }
    // For ePub, we don't have a simple percentage - show as "in progress"
    if (progress.epub_location) return -1; // -1 indicates "in progress" without percentage
    return 0;
  };

  const filterCookbooks = () => {
    let filtered = [...cookbooks];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cookbook =>
        cookbook.title.toLowerCase().includes(query) ||
        (cookbook.notes && cookbook.notes.toLowerCase().includes(query)) ||
        (cookbook.tags && cookbook.tags.toLowerCase().includes(query))
      );
    }

    // Apply tag filter
    if (selectedTag) {
      filtered = filtered.filter(cookbook => {
        if (!cookbook.tags) return false;
        const tags = cookbook.tags.split(',').map(t => t.trim().toLowerCase());
        return tags.includes(selectedTag.toLowerCase());
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "lastAccessed":
          const aProgress = readingProgress[a.id];
          const bProgress = readingProgress[b.id];
          const aDate = aProgress?.last_read_at ? new Date(aProgress.last_read_at) : new Date(0);
          const bDate = bProgress?.last_read_at ? new Date(bProgress.last_read_at) : new Date(0);
          return bDate - aDate; // Most recent first
        case "title":
          return a.title.localeCompare(b.title);
        case "titleDesc":
          return b.title.localeCompare(a.title);
        case "uploadDate":
          return new Date(b.upload_date) - new Date(a.upload_date);
        case "fileSize":
          return b.file_size - a.file_size;
        default:
          return 0;
      }
    });

    setFilteredCookbooks(filtered);
  };

  const handleUploadModalOpen = () => {
    setUploadFormData({ title: "", tags: "", notes: "" });
    setUploadFile(null);
    setUploadProgress(0);
    setShowUploadModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/epub+zip'];
      if (!validTypes.includes(file.type)) {
        setError("Invalid file type. Only PDF and EPUB files are allowed.");
        return;
      }

      // No size limit - accept any file size
      setUploadFile(file);
      setError("");

      // Auto-fill title from filename if empty
      if (!uploadFormData.title) {
        const filename = file.name.replace(/\.(pdf|epub)$/i, '');
        setUploadFormData({ ...uploadFormData, title: filename });
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadFile) {
      setError("Please select a file");
      return;
    }

    if (!uploadFormData.title.trim()) {
      setError("Please enter a title");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadFormData.title.trim());
      formData.append('tags', uploadFormData.tags.trim());
      formData.append('notes', uploadFormData.notes.trim());

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const result = JSON.parse(xhr.response);
          setSuccess("Cookbook uploaded successfully!");
          setShowUploadModal(false);
          fetchCookbooks();
          fetchTags();
          setTimeout(() => setSuccess(""), 3000);
        } else {
          const error = JSON.parse(xhr.response);
          setError(error.error || "Upload failed");
        }
        setUploading(false);
      });

      xhr.addEventListener('error', () => {
        setError("Upload failed. Please try again.");
        setUploading(false);
      });

      xhr.open('POST', `${process.env.REACT_APP_API_URL}/api/cookbooks/upload`);
      xhr.withCredentials = true;
      xhr.send(formData);

    } catch (err) {
      setError(err.message || "Upload failed");
      setUploading(false);
    }
  };

  const handleEditModalOpen = (cookbook) => {
    setSelectedCookbook(cookbook);
    setEditFormData({
      title: cookbook.title,
      tags: cookbook.tags || "",
      notes: cookbook.notes || ""
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!editFormData.title.trim()) {
      setError("Title is required");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${selectedCookbook.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: "include",
          body: JSON.stringify({
            title: editFormData.title.trim(),
            tags: editFormData.tags.trim(),
            notes: editFormData.notes.trim()
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update cookbook");
      }

      setSuccess("Cookbook updated successfully!");
      setShowEditModal(false);
      fetchCookbooks();
      fetchTags();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteModalOpen = (cookbook) => {
    setCookbookToDelete(cookbook);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!cookbookToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/${cookbookToDelete.id}`,
        {
          method: 'DELETE',
          credentials: "include"
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete cookbook");
      }

      setSuccess("Cookbook deleted successfully!");
      setShowDeleteModal(false);
      setCookbookToDelete(null);
      fetchCookbooks();
      fetchTags();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenCookbook = (cookbookId) => {
    navigate(`/cookbook/${cookbookId}`);
  };

  // Google Drive functions
  const checkGoogleAuth = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/google/status`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setGoogleConnected(data.connected);
        if (data.email) {
          setGoogleEmail(data.email);
        }
      }
    } catch (err) {
      console.error("Error checking Google auth status:", err);
    }
  };

  const handleGoogleDriveLinkClick = () => {
    if (!googleConnected) {
      setShowGoogleAuthModal(true);
    } else {
      fetchGoogleDriveFiles();
      setShowGooglePicker(true);
    }
  };

  const handleGoogleAuth = () => {
    // Redirect to Google OAuth flow
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google/drive`;
  };

  const fetchGoogleDriveFiles = async () => {
    setLoadingGoogleFiles(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/google-drive/files`,
        { credentials: "include" }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Google Drive files");
      }

      const data = await response.json();
      setGoogleDriveFiles(data.files || []);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching Google Drive files:", err);
    } finally {
      setLoadingGoogleFiles(false);
    }
  };

  const handleSelectDriveFile = (file) => {
    setSelectedDriveFile(file);
    // Auto-fill title from filename if empty
    if (!linkFormData.title) {
      const filename = file.name.replace(/\.(pdf|epub)$/i, '');
      setLinkFormData({ ...linkFormData, title: filename });
    }
  };

  const handleLinkFromDrive = async (e) => {
    e.preventDefault();

    if (!selectedDriveFile) {
      setError("Please select a file from Google Drive");
      return;
    }

    if (!linkFormData.title.trim()) {
      setError("Please enter a title");
      return;
    }

    setLinking(true);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/cookbooks/link-from-drive`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: "include",
          body: JSON.stringify({
            fileId: selectedDriveFile.id,
            title: linkFormData.title.trim(),
            tags: linkFormData.tags.trim(),
            notes: linkFormData.notes.trim()
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to link cookbook");
      }

      setSuccess("Cookbook linked successfully from Google Drive!");
      setShowGooglePicker(false);
      setSelectedDriveFile(null);
      setLinkFormData({ title: "", tags: "", notes: "" });
      fetchCookbooks();
      fetchTags();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to link cookbook");
    } finally {
      setLinking(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Cache management functions
  const refreshCacheStatus = async () => {
    try {
      const ids = cookbooks.map(c => c.id);
      const status = await getCachedStatusBulk(ids);
      setCachedStatus(status);
    } catch (err) {
      console.error('Error checking cache status:', err);
    }
  };

  const refreshCacheStats = async () => {
    try {
      const stats = await getCacheStats();
      setCacheStats(stats);
    } catch (err) {
      console.error('Error getting cache stats:', err);
    }
  };

  const handleDownloadForOffline = async (cookbook) => {
    if (downloadingForOffline[cookbook.id]) return;

    setDownloadingForOffline(prev => ({ ...prev, [cookbook.id]: { progress: 0, status: 'starting' } }));

    try {
      // Get the file URL
      let fileUrl;
      if (cookbook.storage_type === 'google_drive') {
        fileUrl = `${process.env.REACT_APP_API_URL}/api/cookbooks/${cookbook.id}/proxy`;
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/cookbooks/${cookbook.id}/access-url`,
          { credentials: "include" }
        );
        if (!response.ok) throw new Error("Failed to get access URL");
        const data = await response.json();
        fileUrl = data.url;
      }

      // Download with progress
      const fileResponse = await fetch(fileUrl, { credentials: "include" });
      if (!fileResponse.ok) {
        // Check for Google auth error
        if (fileResponse.status === 401) {
          try {
            const errorData = await fileResponse.json();
            if (errorData.code === 'GOOGLE_AUTH_REQUIRED') {
              throw new Error('GOOGLE_AUTH_REQUIRED');
            }
          } catch (e) {
            if (e.message === 'GOOGLE_AUTH_REQUIRED') throw e;
          }
        }
        throw new Error('Failed to download file');
      }

      const reader = fileResponse.body.getReader();
      const contentLength = parseInt(fileResponse.headers.get('Content-Length')) || cookbook.file_size || 0;
      const chunks = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        const percentage = contentLength > 0 ? Math.round((receivedLength / contentLength) * 100) : 0;
        setDownloadingForOffline(prev => ({
          ...prev,
          [cookbook.id]: { progress: percentage, status: 'downloading' }
        }));
      }

      // Combine chunks and cache
      const blob = new Blob(chunks, {
        type: cookbook.file_type === 'epub' ? 'application/epub+zip' : 'application/pdf'
      });

      await cacheCookbook(cookbook.id, blob, {
        title: cookbook.title,
        fileType: cookbook.file_type
      });

      setSuccess(`"${cookbook.title}" is now available offline!`);
      setTimeout(() => setSuccess(""), 3000);

      // Refresh cache status
      await refreshCacheStatus();
      await refreshCacheStats();

    } catch (err) {
      console.error('Error downloading for offline:', err);
      if (err.message === 'GOOGLE_AUTH_REQUIRED') {
        setGoogleConnected(false);
        setError(`Google Drive access expired. Please reconnect Google Drive to download "${cookbook.title}" for offline use.`);
        checkGoogleAuth(); // Refresh auth status
      } else {
        setError(`Failed to download "${cookbook.title}" for offline use`);
      }
    } finally {
      setDownloadingForOffline(prev => {
        const updated = { ...prev };
        delete updated[cookbook.id];
        return updated;
      });
    }
  };

  const handleUncacheModalOpen = (cookbook) => {
    setCookbookToUncache(cookbook);
    setShowUncacheModal(true);
  };

  const handleRemoveFromCache = async (cookbookId) => {
    try {
      await removeCachedCookbook(cookbookId);
      await refreshCacheStatus();
      await refreshCacheStats();
      setShowUncacheModal(false);
      setCookbookToUncache(null);
      setSuccess("Book removed from cache");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error('Error removing from cache:', err);
      setError("Failed to remove from cache");
    }
  };

  const handleClearAllCache = async () => {
    try {
      await clearAllCache();
      await refreshCacheStatus();
      await refreshCacheStats();
      setShowCacheManagement(false);
      setSuccess("All cached books cleared");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error('Error clearing cache:', err);
      setError("Failed to clear cache");
    }
  };

  const getFileTypeIcon = (fileType) => {
    if (fileType === 'pdf') {
      return <FontAwesomeIcon icon={faFilePdf} className="file-type-icon pdf" />;
    } else {
      return <FontAwesomeIcon icon={faBook} className="file-type-icon epub" />;
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="cookbooks-container mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h1>My Cookbooks</h1>
          <p className="text-muted">Your personal library of PDF and ePub cookbooks</p>
        </Col>
        <Col xs="auto">
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleUploadModalOpen}>
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              Upload from Computer
            </Button>
            <Button variant="outline-primary" onClick={handleGoogleDriveLinkClick}>
              <FontAwesomeIcon icon={faCloud} className="me-2" />
              Link from Google Drive
            </Button>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}

      {/* Cache Status Bar */}
      {cacheStats.bookCount > 0 && (
        <div className="cache-status-bar mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon={faHdd} className="me-2 text-success" />
              <span className="me-3">
                <strong>{cacheStats.bookCount}</strong> book{cacheStats.bookCount !== 1 ? 's' : ''} cached
              </span>
              <span className="text-muted">
                {formatBytes(cacheStats.totalSize)} / {formatBytes(cacheStats.maxSize)}
              </span>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowCacheManagement(true)}
            >
              <FontAwesomeIcon icon={faCog} className="me-1" />
              Manage Cache
            </Button>
          </div>
          <ProgressBar
            now={(cacheStats.totalSize / cacheStats.maxSize) * 100}
            variant={cacheStats.totalSize > cacheStats.maxSize * 0.8 ? 'warning' : 'info'}
            className="mt-2"
            style={{ height: '6px' }}
          />
        </div>
      )}

      {/* Search, Filter, and Sort */}
      <Row className="mb-4">
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Search cookbooks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Col>
        <Col md={4}>
          <Form.Select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
          >
            <option value="">All Tags</option>
            {allTags.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="lastAccessed">Last Accessed</option>
            <option value="title">Title (A-Z)</option>
            <option value="titleDesc">Title (Z-A)</option>
            <option value="uploadDate">Upload Date</option>
            <option value="fileSize">File Size</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Cookbooks Table */}
      {filteredCookbooks.length === 0 ? (
        <div className="text-center mt-5">
          <FontAwesomeIcon icon={faBook} style={{ fontSize: '4rem', color: '#ccc' }} />
          <p className="text-muted mt-3">
            {cookbooks.length === 0
              ? "No cookbooks yet. Upload your first cookbook to get started!"
              : "No cookbooks match your search criteria."}
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover cookbooks-table">
            <thead>
              <tr>
                <th style={{ width: '45%' }}>Title</th>
                <th style={{ width: '10%' }}>Size</th>
                <th style={{ width: '20%' }}>Tags</th>
                <th style={{ width: '25%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCookbooks.map(cookbook => (
                <tr key={cookbook.id} className="cookbook-row">
                  <td>
                    <div className="d-flex align-items-center">
                      {getFileTypeIcon(cookbook.file_type)}
                      <span className="ms-2 fw-medium">{cookbook.title}</span>
                      {cachedStatus[cookbook.id] && (
                        <FontAwesomeIcon icon={faHdd} className="ms-2 text-success" style={{ fontSize: '0.7rem' }} title="Cached" />
                      )}
                      {cookbook.storage_type === 'google_drive' && (
                        <FontAwesomeIcon icon={faCloud} className="ms-1 text-info" style={{ fontSize: '0.7rem' }} title="Google Drive" />
                      )}
                    </div>
                  </td>
                  <td className="text-muted">
                    {formatFileSize(cookbook.file_size)}
                  </td>
                  <td>
                    {cookbook.tags && (
                      <div className="d-flex flex-wrap gap-1">
                        {cookbook.tags.split(',').slice(0, 3).map((tag, idx) => (
                          <Badge key={idx} bg="secondary" className="badge-sm">
                            {tag.trim()}
                          </Badge>
                        ))}
                        {cookbook.tags.split(',').length > 3 && (
                          <Badge bg="light" text="dark" className="badge-sm">
                            +{cookbook.tags.split(',').length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-1 flex-wrap align-items-center">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleOpenCookbook(cookbook.id)}
                      >
                        <FontAwesomeIcon icon={faBookOpen} /> Open
                      </Button>
                      {downloadingForOffline[cookbook.id] && (
                        <div className="d-flex align-items-center gap-2" style={{ minWidth: '120px' }}>
                          <ProgressBar
                            now={downloadingForOffline[cookbook.id].progress}
                            style={{ width: '80px', height: '8px' }}
                            variant="success"
                            animated
                          />
                          <small className="text-muted" style={{ minWidth: '35px' }}>
                            {downloadingForOffline[cookbook.id].progress}%
                          </small>
                        </div>
                      )}
                      {!cachedStatus[cookbook.id] && !downloadingForOffline[cookbook.id] && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleDownloadForOffline(cookbook)}
                          title="Save for offline"
                        >
                          <FontAwesomeIcon icon={faCloudDownloadAlt} />
                        </Button>
                      )}
                      {cachedStatus[cookbook.id] && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleUncacheModalOpen(cookbook)}
                          title="Remove from cache"
                        >
                          <FontAwesomeIcon icon={faTimesCircle} />
                        </Button>
                      )}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleEditModalOpen(cookbook)}
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faPencilAlt} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteModalOpen(cookbook)}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => !uploading && setShowUploadModal(false)} size="lg">
        <Modal.Header closeButton={!uploading}>
          <Modal.Title>Upload Cookbook</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpload}>
            <Form.Group className="mb-3">
              <Form.Label>Select File *</Form.Label>
              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  size="lg"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  type="button"
                >
                  <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                  {uploadFile ? 'Change File' : 'Choose File from Computer'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.epub,application/pdf,application/epub+zip"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  style={{ display: 'none' }}
                  required
                />
              </div>
              <Form.Text className="text-muted">
                Accepted formats: PDF, ePub (unlimited file size)
              </Form.Text>
            </Form.Group>

            {uploadFile && (
              <Alert variant="success">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                <strong>Selected:</strong> {uploadFile.name} ({formatFileSize(uploadFile.size)})
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={uploadFormData.title}
                onChange={(e) => setUploadFormData({ ...uploadFormData, title: e.target.value })}
                disabled={uploading}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Italian, Baking, Quick Meals"
                value={uploadFormData.tags}
                onChange={(e) => setUploadFormData({ ...uploadFormData, tags: e.target.value })}
                disabled={uploading}
              />
              <Form.Text className="text-muted">
                Separate multiple tags with commas
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Personal notes about this cookbook..."
                value={uploadFormData.notes}
                onChange={(e) => setUploadFormData({ ...uploadFormData, notes: e.target.value })}
                disabled={uploading}
              />
            </Form.Group>

            {uploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="progress">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${uploadProgress}%` }}
                    aria-valuenow={uploadProgress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpload} disabled={uploading || !uploadFile}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Cookbook</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEdit}>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Italian, Baking, Quick Meals"
                value={editFormData.tags}
                onChange={(e) => setEditFormData({ ...editFormData, tags: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)}>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Delete Cookbook</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cookbookToDelete && (
            <p>
              Are you sure you want to delete <strong>{cookbookToDelete.title}</strong>?
              This action cannot be undone.
              {cookbookToDelete.storage_type === 'google_drive' && (
                <><br/><br/><em>Note: This will only remove the link from your library. The file will remain in your Google Drive.</em></>
              )}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Google Auth Modal */}
      <Modal show={showGoogleAuthModal} onHide={() => setShowGoogleAuthModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Connect Google Drive</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>To link cookbooks from Google Drive, you need to connect your Google account.</p>
          <p className="text-muted small">
            This will allow you to:
          </p>
          <ul className="text-muted small">
            <li>Browse your PDF and ePub files in Google Drive</li>
            <li>Link cookbooks without uploading to our servers</li>
            <li>Keep files in your Drive while accessing them here</li>
          </ul>
          <p className="text-muted small">
            <strong>Note:</strong> We only request read-only access to your files.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGoogleAuthModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleGoogleAuth}>
            <FontAwesomeIcon icon={faCloud} className="me-2" />
            Connect Google Drive
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Google Drive Picker Modal */}
      <Modal show={showGooglePicker} onHide={() => !linking && setShowGooglePicker(false)} size="lg">
        <Modal.Header closeButton={!linking}>
          <Modal.Title>Link Cookbook from Google Drive</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingGoogleFiles ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading files from Google Drive...</p>
            </div>
          ) : googleDriveFiles.length === 0 ? (
            <Alert variant="info">
              No PDF or ePub files found in your Google Drive.
            </Alert>
          ) : (
            <>
              {!selectedDriveFile ? (
                <>
                  <p className="text-muted mb-3">Select a file from your Google Drive:</p>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {googleDriveFiles.map(file => (
                      <Card
                        key={file.id}
                        className="mb-2"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelectDriveFile(file)}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon
                              icon={file.mimeType === 'application/pdf' ? faFilePdf : faBook}
                              className="me-3"
                              style={{ fontSize: '1.5rem' }}
                            />
                            <div className="flex-grow-1">
                              <div><strong>{file.name}</strong></div>
                              <small className="text-muted">{formatFileSize(file.size || 0)}</small>
                            </div>
                            <FontAwesomeIcon icon={faChevronRight} />
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                <Form onSubmit={handleLinkFromDrive}>
                  <Alert variant="success" className="mb-3">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    <strong>Selected:</strong> {selectedDriveFile.name}
                    <Button
                      variant="link"
                      size="sm"
                      className="float-end"
                      onClick={() => setSelectedDriveFile(null)}
                      disabled={linking}
                    >
                      Change File
                    </Button>
                  </Alert>

                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      value={linkFormData.title}
                      onChange={(e) => setLinkFormData({ ...linkFormData, title: e.target.value })}
                      disabled={linking}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Tags</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Italian, Baking, Quick Meals"
                      value={linkFormData.tags}
                      onChange={(e) => setLinkFormData({ ...linkFormData, tags: e.target.value })}
                      disabled={linking}
                    />
                    <Form.Text className="text-muted">
                      Separate multiple tags with commas
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Personal notes about this cookbook..."
                      value={linkFormData.notes}
                      onChange={(e) => setLinkFormData({ ...linkFormData, notes: e.target.value })}
                      disabled={linking}
                    />
                  </Form.Group>
                </Form>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGooglePicker(false)} disabled={linking}>
            Cancel
          </Button>
          {selectedDriveFile && (
            <Button variant="primary" onClick={handleLinkFromDrive} disabled={linking}>
              {linking ? 'Linking...' : 'Link Cookbook'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Cache Management Modal */}
      <Modal show={showCacheManagement} onHide={() => setShowCacheManagement(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faHdd} className="me-2" />
            Manage Cached Books
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span>Storage Used</span>
              <span>{formatBytes(cacheStats.totalSize)} / {formatBytes(cacheStats.maxSize)}</span>
            </div>
            <ProgressBar
              now={(cacheStats.totalSize / cacheStats.maxSize) * 100}
              variant={cacheStats.totalSize > cacheStats.maxSize * 0.8 ? 'warning' : 'info'}
              style={{ height: '10px' }}
            />
            <small className="text-muted d-block mt-1">
              Books are automatically removed (least recently accessed first) when storage is full.
            </small>
          </div>

          {cacheStats.books.length === 0 ? (
            <Alert variant="info">
              No books cached. Open a book or click "Save Offline" to cache it for instant access.
            </Alert>
          ) : (
            <div>
              <h6 className="mb-3">Cached Books ({cacheStats.bookCount})</h6>
              {cacheStats.books.map(book => (
                <Card key={book.id} className="mb-2">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <strong>{book.title}</strong>
                        <br />
                        <small className="text-muted">
                          {formatBytes(book.fileSize)} - Last accessed {new Date(book.lastAccessedAt).toLocaleDateString()}
                        </small>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleUncacheModalOpen({ id: book.id, title: book.title })}
                      >
                        <FontAwesomeIcon icon={faTimesCircle} />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCacheManagement(false)}>
            Close
          </Button>
          {cacheStats.bookCount > 0 && (
            <Button variant="danger" onClick={handleClearAllCache}>
              <FontAwesomeIcon icon={faTrash} className="me-1" />
              Clear All Cache
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Uncache Confirmation Modal */}
      <Modal show={showUncacheModal} onHide={() => setShowUncacheModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Remove from Cache</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cookbookToUncache && (
            <p>
              Are you sure you want to remove <strong>{cookbookToUncache.title}</strong> from your offline cache?
              <br /><br />
              <em className="text-muted">You can always re-download it later, but you'll need an internet connection to access it.</em>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUncacheModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={() => handleRemoveFromCache(cookbookToUncache?.id)}>
            Remove from Cache
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Cookbooks;
